import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from "react-helmet-async";
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Calendar, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  ArrowLeft, 
  Sparkles, 
  ShieldCheck,
  User,
  GraduationCap
} from 'lucide-react';
import CustomSpinner from '../../components/customerSpinner/CustomSpinner';

const config = require('../../config.json');

export default function ApplicationStatus() {
  const [applicationNo, setApplicationNo] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loaded, setLoaded] = useState(true);
  const [statusMsg, setStatusMsg] = useState('');
  const [searched, setSearched] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleStatusCheck = async (e) => {
    if (e) e.preventDefault();
    if (!applicationNo.trim()) {
      enqueueSnackbar('Please enter your Application Reference Number.', { variant: 'warning' });
      return;
    }
    if (!dateOfBirth) {
      enqueueSnackbar('Please select your Date of Birth.', { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      const clientID = "VJCollege";
      const response = await axios.get(
        `${config.api.invokeURL}/form/registration?clientID=${clientID}&applicationNo=${applicationNo.trim()}&dateOfBirth=${dateOfBirth}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );

      setStatusMsg(response.data);
      setSearched(true);
      if (response.status === 200) {
        enqueueSnackbar("Application status retrieved successfully.", { variant: 'success' });
      } else {
        enqueueSnackbar(response.data, { variant: 'info' });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching status: " + error.message, { variant: 'warning' });
      setStatusMsg("No active application found matching this Application Number and Date of Birth.");
      setSearched(true);
    } finally {
      setLoaded(true);
    }
  };

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Check Application Status - Vidyajyoti College of Theology</title>
        <meta name="description" content="Check the current review and admission status of your online application at Vidyajyoti College of Theology." />
        <link rel="canonical" href="/admission/application-status" />
      </Helmet>

      {!loaded && <CustomSpinner />}

      <div className="container" style={{ maxWidth: '780px' }}>
        {/* Header */}
        <div className="text-center mb-4">
          <div 
            className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-3" 
            style={{ 
              background: 'rgba(26, 43, 86, 0.08)', 
              color: '#1a2b56', 
              fontSize: '0.85rem', 
              fontWeight: 700, 
              border: '1px solid rgba(26, 43, 86, 0.15)' 
            }}
          >
            <ShieldCheck size={16} /> ADMISSIONS VERIFICATION PORTAL
          </div>
          <h1 className="display-6 fw-bold mb-2" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Track Application Status
          </h1>
          <p className="text-muted small mb-0">
            Enter your registration reference number and date of birth to check real-time admission dossier review status.
          </p>
        </div>

        {/* Query Card */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
          <form onSubmit={handleStatusCheck}>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label small fw-bold text-dark">
                  Application Number <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <FileText size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control rounded-end-3 py-2 border-start-0 text-uppercase fw-semibold"
                    placeholder="e.g. 2603301230VJTH"
                    value={applicationNo}
                    onChange={(e) => setApplicationNo(e.target.value.toUpperCase())}
                    required
                  />
                </div>
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Provided upon online registration completion.
                </small>
              </div>

              <div className="col-md-6">
                <label className="form-label small fw-bold text-dark">
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <Calendar size={16} />
                  </span>
                  <input
                    type="date"
                    className="form-control rounded-end-3 py-2 border-start-0"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    required
                  />
                </div>
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                  As entered during application submission.
                </small>
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                className="btn rounded-pill px-5 py-2.5 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                style={{ background: '#1a2b56' }}
              >
                <Search size={18} />
                <span>Check Status</span>
              </button>
            </div>
          </form>

          {/* Status Result Display */}
          {searched && (
            <div className="mt-4 pt-4 border-top">
              <div 
                className="p-4 rounded-4 border text-center"
                style={{ background: 'rgba(26, 43, 86, 0.03)', borderColor: '#1a2b56' }}
              >
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-2 text-white shadow-xs"
                  style={{ width: '48px', height: '48px', background: '#1a2b56' }}
                >
                  <Clock size={22} />
                </div>
                <h6 className="text-muted text-uppercase small fw-bold mb-1" style={{ letterSpacing: '0.05em' }}>
                  Current Registration Status
                </h6>
                <div className="display-7 fw-bold my-2" style={{ color: '#79031d' }}>
                  {statusMsg || 'No record returned'}
                </div>
                <p className="text-muted small mb-0 mx-auto" style={{ maxWidth: '520px' }}>
                  For inquiries or document updates, please email <strong className="text-dark">vjtheology@gmail.com</strong> quoting application ref <strong>{applicationNo}</strong>.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Help & Links */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-2">
          <Link to="/admission/application-form" className="small text-decoration-none text-primary fw-semibold d-inline-flex align-items-center gap-1">
            <ArrowLeft size={14} /> Submit a New Application
          </Link>
          <Link to="/admission/details" className="small text-decoration-none text-muted">
            View Admission Guidelines &amp; Dates →
          </Link>
        </div>
      </div>
    </div>
  );
}
